import { Platform } from 'react-native';
import { storage } from '../utils/storage';
import { API_BASE_URL as BASE_URL } from './apiUrl';
import { setEntryEventSink, settleEntryAttemptOnPageExit, withEntryAttempt } from './entryAttempt';
import { newIdempotencyKey } from './idempotencyKey';
import { installNativeBackgroundFlush } from './nativeBackgroundFlush';

const FLUSH_INTERVAL_MS = 5000;
const MAX_BATCH_SIZE = 50;
const MAX_QUEUE_SIZE = 200;

// ── Сессия (CORE-1137) ──────────────────────────────────────────────────────
//
// До CORE-1137 `sessionId` рождался в конструкторе и умирал вместе с загрузкой
// страницы. Это делало невозможным ровно то, ради чего он вообще есть: связать
// анонимные события с аккаунтом. Регистрация проходит через письмо и вход, то
// есть через новую загрузку страницы, — и «сессия до регистрации» с «сессией
// после» гарантированно оказывались разными строками, которые никакой отчёт не
// сшивает. Заодно `UniqueSessions` в сводке считал не сессии, а перезагрузки:
// после этой правки число закономерно упадёт, и это исправление метрики, а не
// падение трафика.
//
// Теперь сессия живёт в сторадже и обрывается так же, как её понимает вся
// индустрия: после `SESSION_IDLE_MS` бездействия и в любом случае не позже
// `SESSION_MAX_MS`. Без верхней границы «сессией» стала бы вкладка, открытая на
// неделю, и продуктовые воронки перестали бы что-либо значить.
const SESSION_STORAGE_KEY = 'agape_analytics_session_v1';
const SESSION_IDLE_MS = 30 * 60 * 1000;
const SESSION_MAX_MS = 24 * 60 * 60 * 1000;
/** Как часто «последняя активность» доезжает до стораджа. */
const SESSION_TOUCH_THROTTLE_MS = 60 * 1000;

interface StoredSession {
  id: string;
  startedAt: number;
  lastSeenAt: number;
}

interface QueuedEvent {
  /**
   * CORE-1915 — id события, выданный в `track()`. Едет во всех переотправках
   * (пачка вернулась в очередь после отказа fetch, повтор обёртки fetch), и
   * бэкенд по нему пишет одну строку: отказ fetch не значит, что сервер пачку
   * не принял.
   */
  clientEventId: string;
  eventName: string;
  sessionId?: string;
  properties?: Record<string, any>;
  pageUrl?: string;
  timestampUtc: string;
}

function generateSessionId(): string {
  // Simple random ID without uuid dependency
  const chars = 'abcdefghijklmnopqrstuvwxyz0123456789';
  let result = '';
  for (let i = 0; i < 16; i++) {
    result += chars.charAt(Math.floor(Math.random() * chars.length));
  }
  return `${Date.now().toString(36)}-${result}`;
}

class AnalyticsService {
  private queue: QueuedEvent[] = [];
  private flushTimer: ReturnType<typeof setInterval> | null = null;
  private sessionId: string;
  private sessionStartedAt = Date.now();
  private sessionLastSeenAt = Date.now();
  private lastPersistedAt = 0;
  private sessionWasCreated = true;
  private ready: Promise<void>;
  private sessionListeners: Array<(sessionId: string) => void> = [];
  private eventListeners: Array<(eventName: string, properties?: Record<string, any>) => void> = [];
  private isFlushing = false;

  constructor() {
    this.sessionId = generateSessionId();
    this.ready = this.restoreSession();
    this.startFlushing();

    if (Platform.OS === 'web' && typeof window !== 'undefined') {
      // CORE-1548 (QA 16.10.2026) — уход со страницы: не асинхронный `flush`,
      // а синхронная последняя пачка, см. `flushOnUnload`. `pagehide` — для
      // Safari на телефоне, где `beforeunload` может не прийти.
      window.addEventListener('beforeunload', () => this.flushOnUnload());
      // CORE-1956 — незавершённая попытка входа в Live получает исход до
      // последней пачки: слушатель зарегистрирован раньше отправки и
      // срабатывает первым. `beforeunload` не годится — его отменяют.
      window.addEventListener('pagehide', () => settleEntryAttemptOnPageExit('pagehide'));
      window.addEventListener('pagehide', () => this.flushOnUnload());
      // Замороженную вкладку Chrome может выгрузить без `pagehide`, а таймер
      // отправки во сне стоит: исход и пачка уходят на `freeze`.
      if (typeof document !== 'undefined' && typeof document.addEventListener === 'function') {
        document.addEventListener('freeze', () => {
          settleEntryAttemptOnPageExit('freeze');
          this.flushOnUnload();
        });
      }
    }
    // CORE-1957 — native: the in-memory queue goes out before the app may be killed.
    installNativeBackgroundFlush(() => { void this.flush(); });
  }

  /**
   * Последняя пачка перед выгрузкой документа.
   *
   * Обычный `flush` ждёт восстановления сессии и токен из стораджа — два
   * ожидания, после которых документа уже может не быть: клик по QR на главной
   * (переход на `/download` той же вкладкой) до сервера не доезжал, запрос
   * висел в «unknown». Здесь всё синхронно: токен читается сразу
   * (`getTokenSync`, тот же контракт, что у CORE-461), а запрос уходит с
   * `keepalive` — браузер дошлёт его уже после ухода со страницы.
   */
  private flushOnUnload() {
    if (this.queue.length === 0 || typeof fetch !== 'function') return;
    const events = this.queue.splice(0, MAX_BATCH_SIZE);
    const headers: Record<string, string> = { 'Content-Type': 'application/json' };
    const token = storage.getTokenSync();
    if (token) headers['Authorization'] = `Bearer ${token}`;
    try {
      fetch(`${BASE_URL}/api/analytics/events`, {
        method: 'POST',
        headers,
        keepalive: true,
        body: JSON.stringify({
          events: events.map((e) => ({ ...e, sessionId: e.sessionId ?? this.sessionId })),
        }),
      }).catch(() => { /* страница уходит — переотправлять уже некому */ });
    } catch {
      // keepalive не поддержан или тело слишком большое — событий этой пачки
      // не будет, как и раньше; падать на выгрузке нельзя.
    }
  }

  /**
   * Поднять сессию из стораджа, если она ещё живая.
   *
   * Промах (первый запуск, приватный режим, протухшая сессия) — не ошибка:
   * остаётся идентификатор, выданный конструктором, и он же уезжает в сторадж.
   */
  private async restoreSession(): Promise<void> {
    const now = Date.now();
    const stored = await storage
      .getJSON<StoredSession>(SESSION_STORAGE_KEY)
      .catch(() => null);

    const alive = !!stored
      && typeof stored.id === 'string' && stored.id.length > 0
      && now - (stored.lastSeenAt ?? 0) < SESSION_IDLE_MS
      && now - (stored.startedAt ?? 0) < SESSION_MAX_MS;

    if (alive && stored) {
      this.sessionId = stored.id;
      this.sessionStartedAt = stored.startedAt;
      this.sessionWasCreated = false;
    } else {
      this.sessionStartedAt = now;
      this.sessionWasCreated = true;
    }
    this.sessionLastSeenAt = now;
    await this.persistSession(true);
  }

  private async persistSession(force = false): Promise<void> {
    const now = Date.now();
    if (!force && now - this.lastPersistedAt < SESSION_TOUCH_THROTTLE_MS) return;
    this.lastPersistedAt = now;
    try {
      await storage.setJSON<StoredSession>(SESSION_STORAGE_KEY, {
        id: this.sessionId,
        startedAt: this.sessionStartedAt,
        lastSeenAt: this.sessionLastSeenAt,
      });
    } catch {
      // Приватный режим / переполненный сторадж: сессия просто не переживёт
      // перезагрузку. Ронять из-за этого сбор событий незачем.
    }
  }

  /**
   * Отметить активность и, если прежняя сессия истекла, начать новую.
   *
   * Возвращает `true`, когда сессия сменилась, — подписчики (атрибуция,
   * CORE-1137) должны заново пометить новую сессию источником, иначе её
   * события выпадут из разрезов по кампании.
   */
  private touchSession(): boolean {
    const now = Date.now();
    const expired = now - this.sessionLastSeenAt >= SESSION_IDLE_MS
      || now - this.sessionStartedAt >= SESSION_MAX_MS;
    this.sessionLastSeenAt = now;

    if (!expired) {
      void this.persistSession();
      return false;
    }

    this.sessionId = generateSessionId();
    this.sessionStartedAt = now;
    void this.persistSession(true);
    for (const listener of this.sessionListeners) {
      try {
        listener(this.sessionId);
      } catch {
        // Подписчик — вспомогательный слой; его падение не должно уносить
        // событие, которое как раз сейчас пишется.
      }
    }
    return true;
  }

  /** Текущий идентификатор сессии — уже после восстановления из стораджа. */
  async getSessionId(): Promise<string> {
    await this.ready;
    return this.sessionId;
  }

  /**
   * Идентификатор сессии и признак «эта загрузка её и начала».
   *
   * `isNew: false` — сессия поднята из стораджа, то есть это перезагрузка или
   * возврат во вкладку внутри той же сессии. Атрибуция (CORE-1137) по этому
   * признаку решает, писать ли строку `acquisition_touch`: одна строка на
   * сессию, а не на каждую перезагрузку.
   */
  async getSessionInfo(): Promise<{ id: string; isNew: boolean }> {
    await this.ready;
    return { id: this.sessionId, isNew: this.sessionWasCreated };
  }

  /** Подписка на начало новой сессии. Возвращает отписку. */
  onSessionStart(listener: (sessionId: string) => void): () => void {
    this.sessionListeners.push(listener);
    return () => {
      this.sessionListeners = this.sessionListeners.filter((l) => l !== listener);
    };
  }

  /**
   * Подписка на каждое продуктовое событие. Возвращает отписку.
   *
   * Заведена для внешнего маркетингового слоя (CORE-1142): сопоставление
   * «внутреннее событие → цель в кабинете» обязано жить в ОДНОЙ таблице, иначе
   * каждая новая цель добавляется вызовом в очередной экран, и через полгода
   * никто не может ответить, что именно уезжает в Метрику. Подписчик получает
   * событие, но не может его изменить или отменить: внешний слой — наблюдатель
   * над внутренней аналитикой, а не звено в её цепи.
   */
  onEvent(listener: (eventName: string, properties?: Record<string, any>) => void): () => void {
    this.eventListeners.push(listener);
    return () => {
      this.eventListeners = this.eventListeners.filter((l) => l !== listener);
    };
  }

  track(eventName: string, properties?: Record<string, any>) {
    const pageUrl = Platform.OS === 'web' && typeof window !== 'undefined'
      ? window.location.pathname
      : undefined;

    this.touchSession();

    // `sessionId` проставляется не здесь, а при отправке: восстановление из
    // стораджа асинхронно, и события первых миллисекунд запуска иначе унесли бы
    // временный идентификатор, оборвав ту самую связь «до входа → после входа».
    // CORE-1884 — inside a Live room every event carries the entry attempt id.
    properties = withEntryAttempt(properties);
    this.queue.push({
      clientEventId: newIdempotencyKey(),
      eventName,
      properties,
      pageUrl,
      timestampUtc: new Date().toISOString(),
    });

    for (const listener of this.eventListeners) {
      try {
        listener(eventName, properties);
      } catch {
        // Внешний слой не имеет права уронить внутренний сбор: внутренняя
        // аналитика — source of truth, маркетинговая — производная от неё.
      }
    }

    if (this.queue.length >= MAX_BATCH_SIZE) {
      this.flush();
    }
  }

  private startFlushing() {
    this.flushTimer = setInterval(() => this.flush(), FLUSH_INTERVAL_MS);
    // Node returns a Timeout with `unref()`; browsers return a number. We
    // ship `analytics.ts` from app start, so without unref the jest worker
    // (Node) sat on this timer forever — «A worker process has failed to
    // exit gracefully... Active timers can also cause this, ensure that
    // .unref() was called on them.» unref() is a no-op for the FE case
    // (number id has no method, the typeof guard short-circuits).
    const t = this.flushTimer as any;
    if (t && typeof t.unref === 'function') t.unref();
  }

  async flush() {
    if (this.queue.length === 0 || this.isFlushing) return;
    this.isFlushing = true;

    const events = this.queue.splice(0, MAX_BATCH_SIZE);
    try {
      await this.ready;
      const sessionId = this.sessionId;
      const headers: HeadersInit = { 'Content-Type': 'application/json' };
      const token = await storage.getToken();
      if (token) {
        headers['Authorization'] = `Bearer ${token}`;
      }

      await fetch(`${BASE_URL}/api/analytics/events`, {
        method: 'POST',
        headers,
        body: JSON.stringify({
          events: events.map((e) => ({ ...e, sessionId: e.sessionId ?? sessionId })),
        }),
      });
    } catch {
      // Re-queue on failure, drop if queue too large
      if (this.queue.length < MAX_QUEUE_SIZE) {
        this.queue.unshift(...events);
      }
    } finally {
      this.isFlushing = false;
    }
  }

  destroy() {
    if (this.flushTimer) clearInterval(this.flushTimer);
    this.flush();
  }
}

export const analytics = new AnalyticsService();
// CORE-1884 — the entry attempt's own events (started / outcome) go through here.
setEntryEventSink((eventName, properties) => analytics.track(eventName, properties));
